import {
  liveReloadsResponseSchema,
  reloadAlertsResponseSchema,
  reloadAppRunsResponseSchema,
  reloadAppsResponseSchema,
  reloadBottlenecksResponseSchema,
  reloadCollectionResponseSchema,
  reloadErrorsResponseSchema,
  reloadLogResponseSchema,
  reloadPerformanceResponseSchema,
  type LiveReloadsResponse,
  type ReloadAlert,
  type ReloadAppRunsResponse,
  type ReloadAppStats,
  type ReloadBottlenecksResponse,
  type ReloadCollection,
  type ReloadErrorsResponse,
  type ReloadPerformanceResponse,
} from "@repo/shared";

import { API_V1_PREFIX, apiRequestJson } from "../api-client";

const BASE = `${API_V1_PREFIX}/reloads`;

// The period views' filters: ISO instants, the viewer's time zone (hours and
// weekdays are bucketed in it) and an optional origin (Qlik's reload `type`).
export type ReloadPeriodParams = { from: string; to: string; tz: string; type?: string };

function periodQuery(params: ReloadPeriodParams): string {
  const qs = new URLSearchParams({ from: params.from, to: params.to, tz: params.tz });
  if (params.type) qs.set("type", params.type);
  return qs.toString();
}

const path = (connectionId: string, view = "") =>
  `${BASE}/${encodeURIComponent(connectionId)}${view}`;

export async function getReloadCollection(connectionId: string): Promise<ReloadCollection> {
  const res = await apiRequestJson({
    path: path(connectionId),
    responseSchema: reloadCollectionResponseSchema,
    badResponseMessage: "Failed to load reload collection",
  });
  return res.data;
}

export async function setReloadCollection(
  connectionId: string,
  enabled: boolean,
): Promise<ReloadCollection> {
  const res = await apiRequestJson({
    path: path(connectionId, "/collection"),
    method: "PUT",
    body: { enabled },
    responseSchema: reloadCollectionResponseSchema,
    badResponseMessage: "Failed to update reload collection",
  });
  return res.data;
}

export async function getLiveReloads(connectionId: string): Promise<LiveReloadsResponse> {
  return apiRequestJson({
    path: path(connectionId, "/live"),
    responseSchema: liveReloadsResponseSchema,
    badResponseMessage: "Failed to load the reload queue",
  });
}

export async function getReloadErrors(
  connectionId: string,
  params: ReloadPeriodParams,
): Promise<ReloadErrorsResponse["data"]> {
  const res = await apiRequestJson({
    path: `${path(connectionId, "/errors")}?${periodQuery(params)}`,
    responseSchema: reloadErrorsResponseSchema,
    badResponseMessage: "Failed to load reload errors",
  });
  return res.data;
}

export async function getReloadBottlenecks(
  connectionId: string,
  params: ReloadPeriodParams,
): Promise<ReloadBottlenecksResponse["data"]> {
  const res = await apiRequestJson({
    path: `${path(connectionId, "/bottlenecks")}?${periodQuery(params)}`,
    responseSchema: reloadBottlenecksResponseSchema,
    badResponseMessage: "Failed to load reload bottlenecks",
  });
  return res.data;
}

export async function getReloadApps(
  connectionId: string,
  params: ReloadPeriodParams,
): Promise<ReloadAppStats[]> {
  const res = await apiRequestJson({
    path: `${path(connectionId, "/apps")}?${periodQuery(params)}`,
    responseSchema: reloadAppsResponseSchema,
    badResponseMessage: "Failed to load reload apps",
  });
  return res.data;
}

export async function getReloadPerformance(
  connectionId: string,
  params: ReloadPeriodParams,
): Promise<ReloadPerformanceResponse["data"]> {
  const res = await apiRequestJson({
    path: `${path(connectionId, "/performance")}?${periodQuery(params)}`,
    responseSchema: reloadPerformanceResponseSchema,
    badResponseMessage: "Failed to load reload performance",
  });
  return res.data;
}

const appPath = (connectionId: string, appId: string, view: string) =>
  path(connectionId, `/apps/${encodeURIComponent(appId)}${view}`);

export async function getReloadAppRuns(
  connectionId: string,
  appId: string,
  params: ReloadPeriodParams,
): Promise<ReloadAppRunsResponse["data"]> {
  const res = await apiRequestJson({
    path: `${appPath(connectionId, appId, "/runs")}?${periodQuery(params)}`,
    responseSchema: reloadAppRunsResponseSchema,
    badResponseMessage: "Failed to load the app's reloads",
  });
  return res.data;
}

export async function getReloadLog(
  connectionId: string,
  appId: string,
  reloadId: string,
): Promise<string> {
  const res = await apiRequestJson({
    path: appPath(connectionId, appId, `/runs/${encodeURIComponent(reloadId)}/log`),
    responseSchema: reloadLogResponseSchema,
    badResponseMessage: "Failed to load the reload log",
  });
  return res.data.log;
}

export async function getReloadAlerts(connectionId: string): Promise<ReloadAlert[]> {
  const res = await apiRequestJson({
    path: path(connectionId, "/alerts"),
    responseSchema: reloadAlertsResponseSchema,
    badResponseMessage: "Failed to load reload alerts",
  });
  return res.data;
}
